/**
 * LocalStorage Storage Adapter with Quota Management
 * Persists form drafts and records to browser LocalStorage
 * Implements quota exceeded handling and automatic fallback to IndexedDB
 */

import type { StorageAdapter, FormRecord } from '@atlas-forms/types-js';

/**
 * Quota exceeded error details
 */
export interface QuotaExceededError {
  type: 'quota_exceeded';
  attemptedSize: number;
  currentUsage: number;
  limitSize: number;
  fallbackUsed?: boolean;
}

/**
 * Storage metrics for monitoring
 */
export interface StorageMetrics {
  usedBytes: number;
  quotaBytes: number;
  percentageUsed: number;
  warningThreshold: number;
}

/**
 * Callback type for quota warnings
 */
export type QuotaWarningCallback = (metrics: StorageMetrics) => void;

/**
 * LocalStorage-based storage adapter with quota management
 * Suitable for lightweight storage (5-10MB limit)
 * Automatically triggers fallback to IndexedDB on quota exceeded
 */
export class LocalStorageAdapter implements StorageAdapter {
  private readonly draftKeyPrefix: string;
  private readonly recordKeyPrefix: string;
  private readonly quotaWarningThreshold: number;
  private fallbackAdapter: StorageAdapter | null = null;
  private quotaWarningCallback: QuotaWarningCallback | null = null;
  private readonly estimatedQuotaBytes: number = 5 * 1024 * 1024; // 5MB default estimate
  private lastQuotaError: QuotaExceededError | null = null;

  constructor(
    draftKeyPrefix = '@atlas-forms/draft:',
    recordKeyPrefix = '@atlas-forms/record:',
    quotaWarningThreshold = 0.8 // 80% usage triggers warning
  ) {
    this.draftKeyPrefix = draftKeyPrefix;
    this.recordKeyPrefix = recordKeyPrefix;
    this.quotaWarningThreshold = quotaWarningThreshold;
  }

  /**
   * Set fallback storage adapter (e.g., IndexedDB)
   */
  setFallbackAdapter(adapter: StorageAdapter): void {
    this.fallbackAdapter = adapter;
  }

  /**
   * Register callback for quota warnings
   */
  onQuotaWarning(callback: QuotaWarningCallback): void {
    this.quotaWarningCallback = callback;
  }

  /**
   * Get last quota exceeded error
   */
  getLastQuotaError(): QuotaExceededError | null {
    return this.lastQuotaError;
  }

  /**
   * Save a form draft with quota handling and fallback
   */
  async saveDraft(formID: string, values: Record<string, unknown>): Promise<void> {
    try {
      const key = this.getDraftKey(formID);
      const data = {
        formID,
        values,
        savedAt: new Date().toISOString(),
      };
      const serialized = JSON.stringify(data);

      try {
        localStorage.setItem(key, serialized);
        this.checkQuotaWarning();
      } catch (error) {
        if (this.isQuotaExceededError(error)) {
          return this.handleQuotaExceeded(key, serialized, 'draft');
        }
        throw error;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to save draft: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Load a form draft with fallback support
   */
  async loadDraft(formID: string): Promise<Record<string, unknown> | null> {
    try {
      const key = this.getDraftKey(formID);
      const item = localStorage.getItem(key);

      if (!item) {
        // Try fallback adapter if localStorage is empty
        if (this.fallbackAdapter) {
          return this.fallbackAdapter.loadDraft(formID);
        }
        return null;
      }

      const data = JSON.parse(item);
      return data.values;
    } catch (error) {
      // Fallback to IndexedDB on parse error
      if (this.fallbackAdapter) {
        try {
          return this.fallbackAdapter.loadDraft(formID);
        } catch (fallbackError) {
          // Continue with original error
        }
      }

      if (error instanceof Error) {
        throw new Error(`Failed to load draft: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Clear a form draft with fallback support
   */
  async clearDraft(formID: string): Promise<void> {
    try {
      const key = this.getDraftKey(formID);
      localStorage.removeItem(key);

      // Also clear from fallback
      if (this.fallbackAdapter) {
        try {
          await this.fallbackAdapter.clearDraft(formID);
        } catch {
          // Ignore fallback errors when clearing
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to clear draft: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Save a form record with quota handling and fallback
   */
  async saveRecord(record: FormRecord): Promise<void> {
    try {
      const key = this.getRecordKey(record.id);
      const serialized = JSON.stringify(record);

      try {
        localStorage.setItem(key, serialized);
        this.checkQuotaWarning();
      } catch (error) {
        if (this.isQuotaExceededError(error)) {
          return this.handleQuotaExceeded(key, serialized, 'record');
        }
        throw error;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to save record: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get a form record with fallback support
   */
  async getRecord(recordId: string): Promise<FormRecord | null> {
    try {
      const key = this.getRecordKey(recordId);
      const item = localStorage.getItem(key);

      if (!item) {
        // Try fallback adapter
        if (this.fallbackAdapter) {
          return this.fallbackAdapter.getRecord(recordId);
        }
        return null;
      }

      return JSON.parse(item) as FormRecord;
    } catch (error) {
      // Fallback to IndexedDB on parse error
      if (this.fallbackAdapter) {
        try {
          return this.fallbackAdapter.getRecord(recordId);
        } catch (fallbackError) {
          // Continue with original error
        }
      }

      if (error instanceof Error) {
        throw new Error(`Failed to get record: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * List all form drafts from both localStorage and fallback
   */
  async listDrafts(): Promise<Array<{ formID: string; values: Record<string, unknown> }>> {
    try {
      const drafts = [];
      const seenFormIds = new Set<string>();

      // Get from localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.draftKeyPrefix)) {
          const item = localStorage.getItem(key);
          if (item) {
            try {
              const data = JSON.parse(item);
              drafts.push(data);
              seenFormIds.add(data.formID);
            } catch {
              // Skip malformed entries
            }
          }
        }
      }

      // Get from fallback adapter (avoid duplicates)
      if (this.fallbackAdapter) {
        try {
          const fallbackDrafts = await this.fallbackAdapter.listDrafts();
          for (const draft of fallbackDrafts) {
            if (!seenFormIds.has(draft.formID)) {
              drafts.push(draft);
              seenFormIds.add(draft.formID);
            }
          }
        } catch {
          // Ignore fallback errors
        }
      }

      return drafts;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to list drafts: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * List all form records from both localStorage and fallback
   */
  async listRecords(): Promise<FormRecord[]> {
    try {
      const records: FormRecord[] = [];
      const seenIds = new Set<string | number>();

      // Get from localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.recordKeyPrefix)) {
          const item = localStorage.getItem(key);
          if (item) {
            try {
              const record = JSON.parse(item) as FormRecord;
              records.push(record);
              seenIds.add(record.id);
            } catch {
              // Skip malformed entries
            }
          }
        }
      }

      // Get from fallback adapter (avoid duplicates)
      if (this.fallbackAdapter) {
        try {
          const fallbackRecords = await this.fallbackAdapter.listRecords();
          for (const record of fallbackRecords) {
            if (!seenIds.has(record.id)) {
              records.push(record);
              seenIds.add(record.id);
            }
          }
        } catch {
          // Ignore fallback errors
        }
      }

      return records;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to list records: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Delete a specific form record by ID
   */
  async deleteRecord(recordId: string | number): Promise<void> {
    try {
      const key = this.getRecordKey(recordId);
      localStorage.removeItem(key);

      if (this.fallbackAdapter?.deleteRecord) {
        try {
          await this.fallbackAdapter.deleteRecord(recordId);
        } catch {
          // Ignore fallback errors when deleting
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to delete record: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Clear all storage from both localStorage and fallback
   */
  async clear(): Promise<void> {
    try {
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.startsWith(this.draftKeyPrefix) || key.startsWith(this.recordKeyPrefix))
        ) {
          keysToRemove.push(key);
        }
      }

      for (const key of keysToRemove) {
        localStorage.removeItem(key);
      }

      // Also clear fallback
      if (this.fallbackAdapter && this.fallbackAdapter.clear) {
        try {
          await this.fallbackAdapter.clear();
        } catch {
          // Ignore fallback errors
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to clear storage: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get current storage metrics
   */
  async getMetrics(): Promise<StorageMetrics> {
    const usedBytes = await this.getSize();
    const quotaBytes = this.estimatedQuotaBytes;
    const percentageUsed = usedBytes / quotaBytes;

    return {
      usedBytes,
      quotaBytes,
      percentageUsed,
      warningThreshold: this.quotaWarningThreshold,
    };
  }

  /**
   * Get size of stored data in bytes
   */
  async getSize(): Promise<number> {
    try {
      let size = 0;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.startsWith(this.draftKeyPrefix) || key.startsWith(this.recordKeyPrefix))
        ) {
          const item = localStorage.getItem(key);
          if (item) {
            size += item.length + (key.length || 0);
          }
        }
      }

      return size;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Check if quota warning threshold is exceeded
   */
  private checkQuotaWarning(): void {
    if (!this.quotaWarningCallback) {
      return;
    }

    // Estimate size (not calling async getSize for performance)
    let size = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith(this.draftKeyPrefix) || key.startsWith(this.recordKeyPrefix))
      ) {
        const item = localStorage.getItem(key);
        if (item) {
          size += item.length + (key.length || 0);
        }
      }
    }

    const percentageUsed = size / this.estimatedQuotaBytes;
    if (percentageUsed >= this.quotaWarningThreshold) {
      this.quotaWarningCallback({
        usedBytes: size,
        quotaBytes: this.estimatedQuotaBytes,
        percentageUsed,
        warningThreshold: this.quotaWarningThreshold,
      });
    }
  }

  /**
   * Check if error is a QuotaExceededError
   */
  private isQuotaExceededError(error: unknown): boolean {
    if (!(error instanceof Error)) {
      return false;
    }

    const errorName = this.getErrorName(error);
    const message = error.message.toLowerCase();

    return (
      errorName === 'QuotaExceededError' ||
      errorName === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      message.includes('quota') ||
      message.includes('quota exceeded') ||
      message.includes('storage quota exceeded')
    );
  }

  /**
   * Safely get error name from error object
   */
  private getErrorName(error: Error): string {
    return error.name || '';
  }

  /**
   * Handle quota exceeded error with fallback strategy
   */
  private async handleQuotaExceeded(
    key: string,
    serialized: string,
    type: 'draft' | 'record'
  ): Promise<void> {
    const currentUsage = await this.getSize();
    const attemptedSize = serialized.length + key.length;

    this.lastQuotaError = {
      type: 'quota_exceeded',
      attemptedSize,
      currentUsage,
      limitSize: this.estimatedQuotaBytes,
    };

    // Try fallback adapter
    if (this.fallbackAdapter) {
      this.lastQuotaError.fallbackUsed = true;

      if (type === 'draft') {
        const formID = key.replace(this.draftKeyPrefix, '');
        const values = JSON.parse(serialized).values;
        return this.fallbackAdapter.saveDraft(formID, values);
      } else {
        const record = JSON.parse(serialized) as FormRecord;
        return this.fallbackAdapter.saveRecord(record);
      }
    }

    // No fallback available, throw quota error
    throw new Error(
      `LocalStorage quota exceeded (${currentUsage} / ${this.estimatedQuotaBytes} bytes). ` +
        `Cannot save ${type} (${attemptedSize} bytes). ` +
        `No fallback storage configured.`
    );
  }

  /**
   * Generate draft storage key
   */
  private getDraftKey(formID: string): string {
    return `${this.draftKeyPrefix}${formID}`;
  }

  /**
   * Generate record storage key
   */
  private getRecordKey(recordId: string | number): string {
    return `${this.recordKeyPrefix}${recordId}`;
  }
}
